import { ArrowDown, ArrowUp } from 'lucide-react';
import type { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { PredefinedRange } from '@/hooks/use-metrics-date-filter';
import { comparisonLabel, type DeltaView } from '@/lib/period-comparison';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** CMP-01: green (#0A7A45 on 9% opacity) for a delta ≥ 0. */
const UP_STYLE = {
  color: '#0A7A45',
  backgroundColor: 'rgba(10, 122, 69, 0.09)',
};

export type DeltaBadgeProps = {
  delta: DeltaView;
  /** Rendered next to the badge (e.g. {@link ComparisonLabel}); only with a badge. */
  comparison?: ReactNode;
  className?: string;
};

/**
 * Variation against the previous period (CMP-01…03): green with an up arrow
 * when ≥ 0, neutral gray with a down arrow when < 0, "sem dados no período
 * anterior" when the previous value is 0, and nothing when it failed.
 */
export function DeltaBadge({ delta, comparison, className }: DeltaBadgeProps) {
  const { LL } = useI18nContext();

  if (delta.kind === 'none') return null;
  if (delta.kind === 'noBase') {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        {LL.periodComparison.noBase()}
      </span>
    );
  }

  const up = delta.kind === 'up';
  const Arrow = up ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn('inline-flex flex-wrap items-center gap-1.5', className)}
    >
      <Badge
        variant="outline"
        data-kind={delta.kind}
        className={cn(
          'gap-0.5 whitespace-nowrap rounded-sm border-transparent px-1.5 py-0 font-medium tabular-nums',
          !up && 'bg-muted text-muted-foreground'
        )}
        style={up ? UP_STYLE : undefined}
      >
        <Arrow aria-hidden className="size-3" />
        {delta.text}
      </Badge>
      {comparison}
    </span>
  );
}

export type ComparisonLabelProps = {
  preset: PredefinedRange | undefined;
  now: DateTime;
  className?: string;
};

/** "vs. …" label next to a delta (CMP-03). */
export function ComparisonLabel({
  preset,
  now,
  className,
}: ComparisonLabelProps) {
  const { LL } = useI18nContext();
  return (
    <span className={cn('text-xs text-muted-foreground', className)}>
      {comparisonLabel(preset, LL, now)}
    </span>
  );
}
